import { useState, useEffect } from "react";
import YearMonthNavigator from "../components/molecules/YearMonthNavigator";
import LockPeriodForm from "../components/organisms/LockPeriodForm";
import LockPeriodHistoryTable from "../components/organisms/LockPeriodHistoryTable";
import RecordUnlockForm from "../components/organisms/RecordUnlockForm";
import RecordUnlockHistoryTable from "../components/organisms/RecordUnlockHistoryTable";
import useLockStatus from "../hooks/useLockStatus";
import {
  createLockPeriod,
  unlockLockPeriod,
  createRecordUnlock,
  relockRecordUnlock,
  relockAllRecordUnlocks,
} from "../services/lockService";
import { fetchRegiInfo } from "../services/dailyReportService";
import { getCurrentPerson } from "../services/authService";

// YYYYMMDD → YYYY/MM/DD に変換して表示用にする
const formatDateDisplay = (yyyymmdd) =>
  `${yyyymmdd.slice(0, 4)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(6, 8)}`;

// 日本時間の現在年月を YYYYMM 形式で返す
const getJapanYearMonth = () => {
  const jst = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Tokyo" }));
  return `${jst.getFullYear()}${String(jst.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * LockManagementPage - 締め切り操作画面
 *
 * 日付範囲を指定して全店舗一括でデータ入力をロックする（期間単位）。
 * 締め切り後に個別の入力ミス修正が必要な場合は、店舗・レジ・日付を1件指定して
 * 解除する（レコード単位）。期間単位の解除は締め切り登録自体の誤操作の取り消し専用。
 */
function LockManagementPage() {
  const { lockPeriods, recordUnlocks, loading, refetch } = useLockStatus();
  const [regiList, setRegiList] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [missingList, setMissingList] = useState([]);
  const [recordErrorMessage, setRecordErrorMessage] = useState(null);
  // 履歴一覧の年月フィルタ: 操作日時ではなく対象期間・対象日付の年月で絞り込む
  // （締め切り操作自体は当月末日や翌月1〜2日に行われることがあり、操作日時基準だと
  // 同じ意図の操作が月をまたいで分裂して表示されてしまうため）
  const [lockYearMonth, setLockYearMonth] = useState(null);
  const [recordYearMonth, setRecordYearMonth] = useState(null);

  useEffect(() => {
    fetchRegiInfo().then(setRegiList).catch((err) => console.error("レジ情報取得失敗:", err));
  }, []);

  // デフォルト表示月: 直近の登録がある月（StartDateが最大のもの）。データが無ければ今日の年月
  useEffect(() => {
    if (lockYearMonth != null || loading) return;
    if (lockPeriods.length === 0) {
      setLockYearMonth(getJapanYearMonth());
      return;
    }
    const maxStart = lockPeriods.reduce(
      (max, p) => (p.StartDate > max ? p.StartDate : max),
      lockPeriods[0].StartDate,
    );
    setLockYearMonth(maxStart.slice(0, 6));
  }, [lockPeriods, loading, lockYearMonth]);

  // デフォルト表示月: 直近の登録がある月（YYYYMMDDが最大のもの）。データが無ければ今日の年月
  useEffect(() => {
    if (recordYearMonth != null || loading) return;
    if (recordUnlocks.length === 0) {
      setRecordYearMonth(getJapanYearMonth());
      return;
    }
    const maxDate = recordUnlocks.reduce(
      (max, u) => (u.YYYYMMDD > max ? u.YYYYMMDD : max),
      recordUnlocks[0].YYYYMMDD,
    );
    setRecordYearMonth(maxDate.slice(0, 6));
  }, [recordUnlocks, loading, recordYearMonth]);

  const filteredLockPeriods = lockYearMonth
    ? lockPeriods.filter((p) => p.StartDate.slice(0, 6) === lockYearMonth)
    : [];
  const filteredRecordUnlocks = recordYearMonth
    ? recordUnlocks.filter((u) => u.YYYYMMDD.slice(0, 6) === recordYearMonth)
    : [];

  // 有効な部分解除一覧: LockPeriodForm の確認ステップで表示するために店舗名・レジ名を付与する
  const activeRelocks = recordUnlocks
    .filter((u) => u.RelockedAt == null)
    .map((u) => {
      const regi = regiList.find((r) => r.ShopNo === u.ShopNo && r.RegiNo === u.RegiNo);
      return {
        ...u,
        shopName: regi?.ShopName ?? `店舗${u.ShopNo}`,
        regiName: regi?.RegiName ?? `レジ${u.RegiNo}`,
      };
    });

  const handleRegister = async (startDate, endDate) => {
    setProcessing(true);
    setErrorMessage(null);
    setMissingList([]);
    try {
      await createLockPeriod({ startDate, endDate, lockedBy: getCurrentPerson() });
      // 締め切り登録成功後、有効な部分解除が残っていれば一括再ロックする
      const hasActiveUnlocks = recordUnlocks.some((u) => u.RelockedAt == null);
      if (hasActiveUnlocks) {
        await relockAllRecordUnlocks(getCurrentPerson());
      }
      await refetch();
    } catch (err) {
      console.error("ロック登録失敗:", err);
      if (err.missing && err.missing.length > 0) {
        setMissingList(err.missing);
      } else {
        setErrorMessage(`登録に失敗しました: ${err.message}`);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleUnlock = async (id) => {
    if (!window.confirm("このロックを解除しますか？解除すると該当期間のデータが再び編集可能になります。")) {
      return;
    }
    setProcessing(true);
    setErrorMessage(null);
    try {
      await unlockLockPeriod(id, getCurrentPerson());
      await refetch();
    } catch (err) {
      console.error("ロック解除失敗:", err);
      setErrorMessage(`解除に失敗しました: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleRecordUnlockRegister = async ({ shopNo, regiNo, yyyymmdd }) => {
    setProcessing(true);
    setRecordErrorMessage(null);
    try {
      await createRecordUnlock({ shopNo, regiNo, yyyymmdd, unlockedBy: getCurrentPerson() });
      await refetch();
    } catch (err) {
      console.error("レコード単位解除の登録失敗:", err);
      setRecordErrorMessage(`登録に失敗しました: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleRelock = async (id) => {
    if (!window.confirm("このレコードを再ロックしますか？")) {
      return;
    }
    setProcessing(true);
    setRecordErrorMessage(null);
    try {
      await relockRecordUnlock(id, getCurrentPerson());
      await refetch();
    } catch (err) {
      console.error("再ロック失敗:", err);
      setRecordErrorMessage(`再ロックに失敗しました: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="p-6">
      <LockPeriodForm onRegister={handleRegister} disabled={processing} activeRelocks={activeRelocks} />

      {errorMessage && <p className="text-red-600 text-base mb-4">{errorMessage}</p>}

      {missingList.length > 0 && (
        <div className="border border-red-300 bg-red-50 rounded p-4 mb-4">
          <p className="text-red-600 text-base font-medium mb-2">
            未入力のデータが存在するため締め切りできません。以下の店舗・レジに連絡し、入力を済ませてから再度実行してください。
          </p>
          <ul className="text-base text-gray-700 list-disc list-inside">
            {missingList.map((m) => (
              <li key={`${m.shopNo}-${m.regiNo}-${m.yyyymmdd}`}>
                {m.shopName} {m.regiName} {formatDateDisplay(m.yyyymmdd)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <h2 className="text-lg font-bold mb-3">登録履歴</h2>
      {loading || lockYearMonth == null ? (
        <p className="text-gray-400 text-base">読み込み中...</p>
      ) : (
        <>
          <div className="mb-3">
            <YearMonthNavigator yearMonth={lockYearMonth} onChange={setLockYearMonth} />
          </div>
          <LockPeriodHistoryTable
            lockPeriods={filteredLockPeriods}
            onUnlock={handleUnlock}
            disabled={processing}
          />
        </>
      )}

      <hr className="my-8 border-gray-300" />

      <RecordUnlockForm
        regiList={regiList}
        onRegister={handleRecordUnlockRegister}
        disabled={processing}
      />

      {recordErrorMessage && <p className="text-red-600 text-base mb-4">{recordErrorMessage}</p>}

      <h2 className="text-lg font-bold mb-3">レコード単位の解除履歴</h2>
      {loading || recordYearMonth == null ? (
        <p className="text-gray-400 text-base">読み込み中...</p>
      ) : (
        <>
          <div className="mb-3">
            <YearMonthNavigator yearMonth={recordYearMonth} onChange={setRecordYearMonth} />
          </div>
          <RecordUnlockHistoryTable
            recordUnlocks={filteredRecordUnlocks}
            regiList={regiList}
            onRelock={handleRelock}
            disabled={processing}
          />
        </>
      )}
    </div>
  );
}

export default LockManagementPage;
